import { Router } from "express";

import { teacherController } from "../controllers/teacher-controller";
import {
  authenticate,
  requireEmailVerification,
} from "../middleware/auth-middleware";
import { requireRole } from "../middleware/role-middleware";
import {
  attachSchoolContext,
  requireSchoolContext,
} from "../middleware/school-context-middleware";
import {
  validateAddSubject,
  validateChangeTeacherStatus,
  validatePerformanceMetrics,
  validateRejectTeacher,
  validateTeacherFilter,
  validateTerminateTeacher,
  validateUpdateTeacher,
} from "../validation/teacher-validation";

const router = Router();

// Apply authentication and school role requirement to all routes
router.use(authenticate);
router.use(requireEmailVerification);
router.use(requireRole(["school"]));
router.use(attachSchoolContext);
router.use(requireSchoolContext);

/**
 * TEACHER MANAGEMENT ROUTES (School Admin)
 */

/**
 * @route   GET /api/v1/school/teachers
 * @desc    Get all teachers for this specific school with filtering and pagination
 * @access  Private (School Admin)
 * @query   status, isApproved, approvalStatus, subject, page, limit, sortBy, sortOrder, search, hasDocuments, isEmailVerified
 */
router.get("/", validateTeacherFilter, (req, res) =>
  teacherController.getAllSchoolTeachers(req, res),
);

/**
 * @route   GET /api/v1/school/teachers/statistics
 * @desc    Get comprehensive statistics for teachers in this school
 * @access  Private (School Admin)
 */
router.get("/statistics", (req, res) =>
  teacherController.getSchoolTeacherStatistics(req, res),
);

/**
 * @route   GET /api/v1/school/teachers/pending
 * @desc    Get teachers by approval status with advanced filtering and pagination
 * @access  Private (School Admin)
 * @query   approvalStatus, status, subject, search, page, limit, sortBy, sortOrder, dateFrom, dateTo, hasDocuments, isEmailVerified
 */
router.get("/pending", validateTeacherFilter, (req, res) =>
  teacherController.getTeachersByStatus(req, res),
);

/**
 * @route   GET /api/v1/school/teachers/by-status
 * @desc    Get teachers by approval status with advanced filtering and pagination (alternative endpoint)
 * @access  Private (School Admin)
 * @query   approvalStatus, status, subject, search, page, limit, sortBy, sortOrder, dateFrom, dateTo, hasDocuments, isEmailVerified
 */
router.get("/by-status", validateTeacherFilter, (req, res) =>
  teacherController.getTeachersByStatus(req, res),
);

/**
 * @route   GET /api/v1/school/teachers/statistics
 * @desc    Get teacher statistics
 * @access  Private (School Admin)
 */
router.get("/statistics", (req, res) =>
  teacherController.getTeacherStatistics(req, res),
);

/**
 * @route   GET /api/v1/school/teachers/by-subject/:subject
 * @desc    Get teachers by subject
 * @access  Private (School Admin)
 */
router.get("/by-subject/:subject", (req, res) =>
  teacherController.getTeachersBySubject(req, res),
);

/**
 * @route   GET /api/v1/school/teachers/:id
 * @desc    Get teacher by ID with full details
 * @access  Private (School Admin)
 */
router.get("/:id", (req, res) => teacherController.getTeacherById(req, res));

/**
 * @route   PUT /api/v1/school/teachers/:id
 * @desc    Update teacher details
 * @access  Private (School Admin)
 */
router.put("/:id", validateUpdateTeacher, (req, res) =>
  teacherController.updateTeacher(req, res),
);

/**
 * @route   DELETE /api/v1/school/teachers/:id
 * @desc    Delete teacher
 * @access  Private (School Admin)
 */
router.delete("/:id", (req, res) => teacherController.deleteTeacher(req, res));

/**
 * TEACHER APPROVAL ROUTES
 */

/**
 * @route   POST /api/v1/school/teachers/bulk-approve
 * @desc    Bulk approve multiple teachers
 * @access  Private (School Admin)
 * @body    { teacherIds: string[] }
 */
router.post("/bulk-approve", (req, res) =>
  teacherController.bulkApproveTeachers(req, res),
);

/**
 * @route   POST /api/v1/school/teachers/bulk-reject
 * @desc    Bulk reject multiple teachers with reason
 * @access  Private (School Admin)
 * @body    { teacherIds: string[], reason: string }
 */
router.post("/bulk-reject", (req, res) =>
  teacherController.bulkRejectTeachers(req, res),
);

/**
 * @route   PUT /api/v1/school/teachers/:id/approve
 * @desc    Approve teacher
 * @access  Private (School Admin)
 */
router.put("/:id/approve", (req, res) =>
  teacherController.approveTeacher(req, res),
);

/**
 * @route   PUT /api/v1/school/teachers/:id/reject
 * @desc    Reject teacher with reason
 * @access  Private (School Admin)
 * @body    { reason: string }
 */
router.put("/:id/reject", validateRejectTeacher, (req, res) =>
  teacherController.rejectTeacher(req, res),
);

/**
 * @route   PUT /api/v1/school/teachers/:id/status
 * @route   POST /api/v1/school/teachers/:id/status (alias for compatibility)
 * @desc    Change teacher approval status (approve/reject) in one API
 * @access  Private (School Admin)
 * @body    { status: "approve" | "reject", reason?: string }
 */
router.put("/:id/status", validateChangeTeacherStatus, (req, res) =>
  teacherController.changeTeacherStatus(req, res),
);
router.post("/:id/status", validateChangeTeacherStatus, (req, res) =>
  teacherController.changeTeacherStatus(req, res),
);

/**
 * TEACHER STATUS MANAGEMENT ROUTES
 */

/**
 * @route   PUT /api/v1/school/teachers/:id/terminate
 * @desc    Terminate teacher with reason
 * @access  Private (School Admin)
 * @body    { reason: string }
 */
router.put("/:id/terminate", validateTerminateTeacher, (req, res) =>
  teacherController.terminateTeacher(req, res),
);

/**
 * @route   PUT /api/v1/school/teachers/:id/leave
 * @desc    Put teacher on leave
 * @access  Private (School Admin)
 */
router.put("/:id/leave", (req, res) => teacherController.putOnLeave(req, res));

/**
 * @route   PUT /api/v1/school/teachers/:id/reactivate
 * @desc    Reactivate teacher
 * @access  Private (School Admin)
 */
router.put("/:id/reactivate", (req, res) =>
  teacherController.reactivateTeacher(req, res),
);

/**
 * TEACHER SUBJECT MANAGEMENT ROUTES
 */

/**
 * @route   POST /api/v1/school/teachers/:id/subjects
 * @desc    Add subject to teacher
 * @access  Private (School Admin)
 * @body    { name: string, qualificationLevel?: string, yearsExperience?: number, certifications?: string[] }
 */
router.post("/:id/subjects", validateAddSubject, (req, res) =>
  teacherController.addSubject(req, res),
);

/**
 * @route   DELETE /api/v1/school/teachers/:id/subjects/:subjectName
 * @desc    Remove subject from teacher
 * @access  Private (School Admin)
 */
router.delete("/:id/subjects/:subjectName", (req, res) =>
  teacherController.removeSubject(req, res),
);

/**
 * TEACHER CLASS MANAGEMENT ROUTES
 */

/**
 * @route   GET /api/v1/school/teachers/:id/classes
 * @desc    Get teacher's assigned classes
 * @access  Private (School Admin)
 * @query   status, semesterId
 */
router.get("/:id/classes", (req, res) =>
  teacherController.getTeacherClasses(req, res),
);

/**
 * TEACHER DOCUMENT VERIFICATION ROUTES
 */

/**
 * @route   PUT /api/v1/school/teachers/:id/documents/:fileName/verify
 * @desc    Verify teacher document
 * @access  Private (School Admin)
 */
router.put("/:id/documents/:fileName/verify", (req, res) =>
  teacherController.verifyDocument(req, res),
);

/**
 * TEACHER PERFORMANCE ROUTES
 */

/**
 * @route   PUT /api/v1/school/teachers/:id/performance
 * @desc    Update teacher performance metrics
 * @access  Private (School Admin)
 * @body    { totalClasses?: number, totalStudents?: number, averageRating?: number, completedAssignments?: number, attendanceRate?: number }
 */
router.put("/:id/performance", validatePerformanceMetrics, (req, res) =>
  teacherController.updatePerformanceMetrics(req, res),
);

export { router as teacherRoutes };
